import React, { useState } from "react";
import { Form, Button, message } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";
import { AuthLayout } from "@/shared/layout";
import { PasswordInputWithValidation } from "@/features/auth";
import { validatePassword } from "@/shared/utils/passwordValidation";
import { userService } from "@/api/user";
import OrganizationResetPassword from "./OrganizationResetPassword";

const StyledForm = styled(Form)`
	.ant-form-item {
		margin-bottom: 1.5rem;
	}

	.ant-form-item-label {
		padding-bottom: 0.5rem;
	}

	.ant-form-item-label > label {
		font-weight: 500;
		color: #333;
	}

	.ant-input-affix-wrapper {
		height: 47px;
		border-radius: 50px;
		border: 1px solid #d9d9d9;

		&:hover,
		&:focus {
			border-color: ${CUSTOM_COLORS.PRIMARY};
		}
	}

	.ant-input {
		font-size: 0.9rem;
	}
`;

const StyledPasswordInput = styled(PasswordInputWithValidation)`
	.ant-input-affix-wrapper {
		height: 47px;
		border-radius: 50px;
		border: 1px solid #d9d9d9;

		&:hover,
		&:focus {
			border-color: ${CUSTOM_COLORS.PRIMARY};
		}
	}
`;

const SubmitButton = styled(Button)`
	height: 36px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	background: ${CUSTOM_COLORS.PRIMARY};
	border: none;
	width: 100%;

	&:hover {
		background: ${CUSTOM_COLORS.PRIMARY} !important;
		opacity: 0.9;
	}
	&:disabled {
		background: #ebebe4 !important;
		opacity: 0.9;
	}
`;

const ErrorMessage = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
	padding: 12px 14px;
	border-radius: 8px;
	background: #fff1f0;
	border: 1px solid #ffa39e;
	color: #a8071a;
	margin-bottom: 16px;

	.error-icon {
		font-size: 18px;
		color: #cf1322;
		margin-top: 2px;
	}

	.error-content {
		display: flex;
		flex-direction: column;
	}

	.error-title {
		font-weight: 600;
		margin-bottom: 2px;
	}

	.error-message {
		font-size: 13px;
	}
`;

const StepTransition = styled(motion.div)`
	width: 100%;
`;

const SuccessIconWrapper = styled.div`
	display: flex;
	justify-content: center;
	margin-bottom: 1rem;

	.icon-circle {
		width: 64px;
		height: 64px;
		border-radius: 50%;
		background: ${CUSTOM_COLORS.PRIMARY}1A;
		display: flex;
		align-items: center;
		justify-content: center;
		color: ${CUSTOM_COLORS.PRIMARY};
		font-size: 28px;
	}
`;

const LoginLink = styled(Link)`
	color: var(--antd-primary);
	font-size: 0.875rem;
	text-decoration: none;

	&:hover {
		color: ${CUSTOM_COLORS.PRIMARY};
	}
`;

interface ResetPasswordWithTokenProps {
	token: string;
}

const ResetPasswordWithToken: React.FC<ResetPasswordWithTokenProps> = ({ token }) => {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [isPasswordValid, setIsPasswordValid] = useState(false);
	const [isConfirmValid, setIsConfirmValid] = useState(false);
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [done, setDone] = useState(false);

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setPassword(value);
		setIsPasswordValid(validatePassword(value).isValid);
		// Re-validate confirm if user changes the primary password
		setIsConfirmValid(confirmPassword.length > 0 && confirmPassword === value);
	};

	const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setConfirmPassword(value);
		setIsConfirmValid(value === password && value.length > 0);
	};

	const onFinish = async () => {
		setErrorMessage("");
		if (!isPasswordValid || !isConfirmValid) return;
		setLoading(true);
		try {
			await userService.resetPassword(token, password);
			setDone(true);
			message.success("Password reset successfully. Redirecting to log in...");
			setTimeout(() => navigate("/login"), 1500);
		} catch (error: any) {
			const errMsg =
				error?.response?.data?.message ||
				error?.message ||
				"Failed to reset password. The link may have expired.";
			setErrorMessage(errMsg);
		} finally {
			setLoading(false);
		}
	};

	if (done) {
		return (
			<AuthLayout title="" subtitle="" backLinkTo="/login">
				<div style={{ textAlign: "center", padding: "1rem 0 2rem" }}>
					<SuccessIconWrapper>
						<div className="icon-circle">
							<CheckCircleOutlined />
						</div>
					</SuccessIconWrapper>
					<h2 style={{ margin: "0 0 0.5rem 0", fontSize: 24, fontWeight: 600 }}>Password reset</h2>
					<p style={{ color: "#555", margin: 0 }}>Your password has been reset successfully.</p>
					<p style={{ color: "#555", marginTop: 4 }}>Redirecting you to log in…</p>
				</div>
			</AuthLayout>
		);
	}

	return (
		<AuthLayout
			title="Set a new password"
			subtitle="Choose a strong password for your account."
			backLinkTo="/login"
		>
			<div style={{ display: "block", minHeight: errorMessage === "" ? 0 : 100 }}>
				<AnimatePresence mode="wait" initial={false}>
					{!!errorMessage && (
						<StepTransition
							key="reset-error"
							initial={{ opacity: 0, y: -6 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -6 }}
							transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
						>
							<ErrorMessage>
								<CloseCircleOutlined className="error-icon" />
								<div className="error-content">
									<div className="error-title">Error</div>
									<div className="error-message">{errorMessage}</div>
								</div>
							</ErrorMessage>
						</StepTransition>
					)}
				</AnimatePresence>
			</div>

			<StyledForm form={form} name="resetPasswordWithToken" onFinish={onFinish} layout="vertical">
				<Form.Item
					name="password"
					label="New password"
					style={{ marginTop: "1.5rem" }}
					rules={[
						{
							validator: (_, value) => {
								if (!value) {
									return Promise.reject(new Error("Please enter a password"));
								}
								const validation = validatePassword(value);
								if (!validation.isValid) {
									return Promise.reject(new Error(validation.errors[0]));
								}
								return Promise.resolve();
							},
						},
					]}
				>
					<StyledPasswordInput
						value={password}
						onChange={handlePasswordChange}
						placeholder="Enter new password"
						size="large"
						showValidation={true}
					/>
				</Form.Item>

				<Form.Item
					name="confirmPassword"
					label="Confirm new password"
					rules={[
						{
							validator: (_, value) => {
								if (!value) {
									return Promise.reject(new Error("Please confirm your password"));
								}
								if (value !== password) {
									return Promise.reject(new Error("Passwords do not match!"));
								}
								return Promise.resolve();
							},
						},
					]}
				>
					<StyledPasswordInput
						value={confirmPassword}
						onChange={handleConfirmPasswordChange}
						placeholder="Re-enter new password"
						size="large"
						showValidation={false}
					/>
				</Form.Item>

				<Form.Item>
					<SubmitButton
						type="primary"
						htmlType="submit"
						loading={loading}
						disabled={!isPasswordValid || !isConfirmValid || loading}
					>
						Reset password
					</SubmitButton>
				</Form.Item>

				<div
					style={{
						textAlign: "center",
						marginTop: "0.5rem",
						marginBottom: "2rem",
						fontSize: "0.875rem",
					}}
				>
					Remember your password? <LoginLink to="/login">Log in</LoginLink>
				</div>
			</StyledForm>
		</AuthLayout>
	);
};

const ResetPassword: React.FC = () => {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");

	if (!token) {
		return <OrganizationResetPassword />;
	}

	return <ResetPasswordWithToken token={token} />;
};

export default ResetPassword;
